import { observable, flow, makeObservable } from "mobx";

import { EAIClient } from "Services/EAIClient";
import { appStore } from "../";

export interface StrategicPlan {
    result: {
        code: number,
        message: string
    },
    data: StrategicPlanParent[]
}

export interface StrategicPlanParent {
    ruuid: string,
    school_id: string,
    name: string,
    status: string,
    start_date: string,
    end_date: string,
    enflux_created: string,
    enflux_updated: string,
    items: StrategicPlanItem[]
    action_plan_statuses?: ActionPlanStatuses,
}

export interface ActionPlanStatuses {
    open_time: number,
    open_late: number,
    closed_goal_met: number,
    closed_goal_not_met: number,
    no_date_assigned: number
}

export interface StrategicPlanItem {
    id: number | string,
    school_id: string,
    name: string,
    status: string,
    parent_id: number,
    strategic_plan_ruuid: string,
    created: string,
    updated: string,
    children: StrategicPlanItem[],
    action_plan_statuses?: ActionPlanStatuses,
    total_children_action_plan_statuses?: ActionPlanStatuses,
    newItem?: boolean,
    level?: number
    display_order: number
}

export interface StrategicPlanRequest {
    name: string,
    status?: string,
    start_date: string,
    end_date: string,
}

export interface StrategicPlanItemRequest {
    name: string,
    status?: string,
    parent_id: number | null,
    display_order?: number
}


export interface IndividualItem {
    id: number,
    school_id: string,
    name: string,
    status: string | null,
    parent_id: number | null,
    strategic_plan_ruuid: string | null,
    created: string,
    updated: string,
    strategic_plan_name: string | null,
    level: string;
}

interface InputItem {
    id: number;
    school_id: string;
    name: string;
    status: string | null;
    parent_id: number | null;
    strategic_plan_ruuid: string | null;
    created: string;
    updated: string;
    strategic_plan_name: string | null;
    children: InputItem[];
  }

function transformArray(inputArray: InputItem[]): IndividualItem[] {
    const outputArray: IndividualItem[] = [];
  
    function transformItem(item: InputItem, level: string) {
      const outputItem: IndividualItem = {
        id: item.id,
        school_id: item.school_id,
        name: item.name,
        status: item.status,
        parent_id: item.parent_id,
        strategic_plan_ruuid: item.strategic_plan_ruuid,
        created: item.created,
        updated: item.updated,
        strategic_plan_name: item.strategic_plan_name,
        level: level,
      };
  
      outputArray.push(outputItem);
  
      if (item.children && item.children.length > 0) {
        item.children.forEach((child) => transformItem(child, getNextLevel(level)));
      }
    }
  
    function getNextLevel(currentLevel: string): string {
      const levelOrder = ['first', 'second', 'third', 'fourth'];
      const currentIndex = levelOrder.indexOf(currentLevel);
      const nextIndex = currentIndex + 1;
      return nextIndex < levelOrder.length ? levelOrder[nextIndex] : currentLevel;
    }
  
    inputArray.forEach((item) => transformItem(item, 'first'));
  
    return outputArray;
}
class StrategicPlanStore {

    @observable isProcessing = false;
    @observable strategicPlanItem: StrategicPlanItem;
    @observable strategicPlanResponse: string;
    @observable strategicPlanItems: StrategicPlanParent[] = [];
    @observable individualItems: IndividualItem[] = [];
    @observable totalCountStrategicPlans: number;

    client = new EAIClient();

    constructor() {
        makeObservable(this);
    }

    @flow
    * fetchStrategicPlanItems(flatten?: boolean): any {
        this.isProcessing = true;
        const flattenValue = flatten ?? false;
        try {
            const resp = yield this.client.getStrategicPlans(flattenValue);
            this.strategicPlanItems = resp.data.data;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * fetchStrategicPlanItemsOnly(): any {
        this.isProcessing = true;
        try {
            const resp = yield this.client.getStrategicPlans(false, true);
            this.individualItems = transformArray(resp.data.data.items);
            this.totalCountStrategicPlans = resp.data.data.total_count_strategic_plans;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * createStrategicPlan(strategicPlan: StrategicPlanRequest): any {
        this.isProcessing = true;
        try {
            const resp = yield this.client.createStrategicPlan(strategicPlan);
            this.strategicPlanResponse = resp.data.data.ruuid;

        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * createStrategicPlanItem(strategic_plan_id: string, item: StrategicPlanItemRequest): any {
        this.isProcessing = true;
        try {
            yield this.client.createStrategicPlanItem(strategic_plan_id, item);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * updateStrategicPlanItem(strategic_plan_id: string, strategic_plan: StrategicPlanRequest): any {
        this.isProcessing = true;
        try {
            yield this.client.updateStrategicPlan(strategic_plan_id, strategic_plan);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * deleteStrategicPlanItem(id: number): any {
        this.isProcessing = true;
        try {
            yield this.client._deleteStrategicPlanItem(id);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * archiveStrategicPlanItem(id: number, status:string, strategic_plan_ruuid: string): any {
        this.isProcessing = true;
        try {
            yield this.client.archiveStrategicPlanItem(id, status, strategic_plan_ruuid);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * archiveStrategicPlan(strategic_plan_ruuid: string, status:string): any {
        this.isProcessing = true;
        try {
            yield this.client.archiveStrategicPlan(status, strategic_plan_ruuid);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * copyStrategicPlan(name:string,strategic_plan_ruuid: string): any {
        this.isProcessing = true;
        try {
            yield this.client.copyStrategicPlan(name, strategic_plan_ruuid);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }
}

export { StrategicPlanStore };